import { api } from './api.js';
import type { DispositivoPush } from '@matias-asset-os/contracts';

/**
 * AVISOS EN ESTE DISPOSITIVO.
 *
 * Tres cosas tienen que estar bien, y las tres fallan por motivos distintos,
 * asi que cada una dice lo suyo en vez de un «no se pudo» general:
 *
 *   1. El navegador tiene que saber de avisos (`serviceWorker` y `PushManager`).
 *      En el iPhone eso SOLO existe con la aplicacion añadida a la pantalla de
 *      inicio; en Safari con una pestaña abierta no hay nada que activar, y no
 *      lo habra por mucho que se pulse. Por eso ese caso se detecta aparte: no
 *      es «este navegador no puede», es «te falta un paso».
 *   2. La persona tiene que dar permiso, y aqui esta la trampa que tenia esto
 *      roto: en el iPhone el permiso SOLO se puede pedir dentro del toque que
 *      lo pide. Si antes de pedirlo se hace una llamada al servidor y se espera
 *      la respuesta, el toque ya se ha gastado y el aviso no sale nunca — sin
 *      error, sin nada. Por eso la clave se trae ANTES y `encender` la recibe
 *      hecha: lo primero que hace es pedir el permiso.
 *   3. El servidor tiene que tener su clave configurada.
 */

export type EstadoAvisos =
  | 'no-soportado'
  | 'instalar'
  | 'sin-clave'
  | 'denegado'
  | 'apagado'
  | 'encendido';

export interface Diagnostico {
  estado: EstadoAvisos;
  /** Los dispositivos que el SERVIDOR cree que tiene esta persona. */
  dispositivos: DispositivoPush[];
  /** La clave del servidor, ya traida, para poder encender sin esperas. */
  clave: string | null;
}

/** Lo que el navegador entrega como clave hay que pasarlo a bytes. */
function bytesDeClave(base64: string): Uint8Array {
  const normal = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const crudo = atob(normal);
  return Uint8Array.from([...crudo].map((c) => c.charCodeAt(0)));
}

function aBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

export function soporta(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** Un iPhone o un iPad, incluidos los que se hacen pasar por Mac. */
function esApple(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Abierta desde la pantalla de inicio, sin barra de navegador. */
export function instalada(): boolean {
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

/** El registro se hace una vez y se reutiliza. */
async function trabajador(): Promise<ServiceWorkerRegistration> {
  const existente = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existente) return existente;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

async function suscripcionActual(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null;
  const registro = await navigator.serviceWorker.getRegistration('/sw.js');
  return (await registro?.pushManager.getSubscription()) ?? null;
}

/**
 * En que punto esta este dispositivo, y que sabe el servidor de el.
 *
 * Se devuelve TODO junto a proposito: la pantalla tiene que poder decir «tu
 * telefono no esta en la lista» sin hacer otra vuelta, y `encender` necesita la
 * clave ya en la mano para no gastar el toque.
 */
export async function diagnosticar(): Promise<Diagnostico> {
  const suscripcion = await suscripcionActual().catch(() => null);

  let clave: string | null = null;
  let dispositivos: DispositivoPush[] = [];
  try {
    const r = await api.pushClave(suscripcion?.endpoint);
    clave = r.clave;
    dispositivos = r.dispositivos;
  } catch {
    /* sin servidor no hay nada que decir del servidor; el resto sigue valiendo */
  }

  const estado = ((): EstadoAvisos => {
    // El iPhone sin instalar no es «no se puede»: es «falta un paso».
    if (!soporta()) return esApple() && !instalada() ? 'instalar' : 'no-soportado';
    if (!clave) return 'sin-clave';
    if (Notification.permission === 'denied') return 'denegado';
    return suscripcion ? 'encendido' : 'apagado';
  })();

  return { estado, dispositivos, clave };
}

/**
 * Enciende los avisos en ESTE dispositivo.
 *
 * Recibe la clave ya traida porque lo primero que hace —lo primero de todo—
 * tiene que ser pedir el permiso: en el iPhone, cualquier espera antes de eso
 * gasta el toque y el permiso no se llega a pedir.
 */
export async function encender(clave: string | null): Promise<EstadoAvisos> {
  if (!soporta()) return esApple() && !instalada() ? 'instalar' : 'no-soportado';
  if (!clave) return 'sin-clave';

  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return permiso === 'denied' ? 'denegado' : 'apagado';

  const registro = await trabajador();
  await navigator.serviceWorker.ready;
  const suscripcion =
    (await registro.pushManager.getSubscription()) ??
    (await registro.pushManager.subscribe({
      // Sin esto el navegador no suscribe: todo aviso ha de verse.
      userVisibleOnly: true,
      applicationServerKey: bytesDeClave(clave) as BufferSource,
    }));

  await api.pushSuscribir({
    endpoint: suscripcion.endpoint,
    clavep256dh: aBase64(suscripcion.getKey('p256dh')),
    claveAuth: aBase64(suscripcion.getKey('auth')),
    dispositivo: nombreDelDispositivo(),
  });
  return 'encendido';
}

/**
 * Como se llama este aparato en la lista.
 *
 * El `userAgent` entero es ilegible y no distingue nada: en la pantalla hay que
 * poder mirar la lista y saber cual es el telefono y cual el ordenador de la
 * oficina.
 */
function nombreDelDispositivo(): string {
  const ua = navigator.userAgent;
  const aparato = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : 'Navegador';
  const navegador = /CriOS|Chrome/.test(ua)
    ? 'Chrome'
    : /Edg/.test(ua)
      ? 'Edge'
      : /Firefox|FxiOS/.test(ua)
        ? 'Firefox'
        : 'Safari';
  return `${aparato} · ${instalada() ? 'app' : navegador}`.slice(0, 200);
}

/** Apaga los avisos en este dispositivo; los demas siguen. */
export async function apagar(): Promise<EstadoAvisos> {
  if (!soporta()) return 'no-soportado';
  const suscripcion = await suscripcionActual();
  if (suscripcion) {
    await api.pushBaja(suscripcion.endpoint).catch(() => undefined);
    await suscripcion.unsubscribe().catch(() => undefined);
  }
  return 'apagado';
}
